import { useMutation, useQueryClient } from '@tanstack/react-query';
import { documentService } from '@/services/documents/DocumentService';
import type { GenerateDocumentResponse } from '@/services/documents/DocumentService';
import { entityService } from '@/services/EntityService';

export interface RegenerateDocumentArgs {
  documentType: string;
  payload: Record<string, unknown>;
  /** Original options from the saved payload. skipStorage/skipRegister are
   *  intentionally stripped — regeneration always stores and registers the
   *  document so it's available for download later. */
  options?: {
    skipStorage?: boolean;
    skipRegister?: boolean;
    metadata?: Record<string, unknown>;
  };
  /** When regenerating from a transaction, link the new document to this transaction. */
  transactionId?: string;
  /** Pre-assigned document numbers from the original generation. */
  nonExpendableDocumentNumber?: string;
  expendableDocumentNumber?: string;
}

export const useRegenerateDocument = () => {
  const queryClient = useQueryClient();

  return useMutation<GenerateDocumentResponse, Error, RegenerateDocumentArgs>({
    mutationFn: async ({
      documentType,
      payload,
      options,
      transactionId,
      nonExpendableDocumentNumber,
      expendableDocumentNumber,
    }: RegenerateDocumentArgs) => {
      // Strip skipStorage and skipRegister — regeneration must always
      // persist the document so it's available for future retrieval.
      const { skipStorage: _s, skipRegister: _r, ...safeOptions } = options ?? {};
      const response = await documentService.generateDocument({
        documentType,
        payload,
        options: {
          ...safeOptions,
          metadata: {
            ...safeOptions.metadata,
            regeneratedAt: new Date().toISOString(),
          },
        },
        nonExpendableDocumentNumber,
        expendableDocumentNumber,
      });

      // Link the regenerated document entity to the transaction so it
      // appears in the transaction's document list.
      if (transactionId && response.documentEntityId) {
        try {
          await entityService.updateEntity(response.documentEntityId, {
            entity_data: { transaction_id: transactionId },
          });
        } catch {
          // Best-effort — document was generated but linking failed
        }
      }

      return response;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['transaction-log'] });
      void queryClient.invalidateQueries({ queryKey: ['entities'] });
    },
  });
};
